import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    Loader2, CheckCircle2, CreditCard, DollarSign, 
    Trash2, AlertCircle, Zap, Info, ArrowLeft 
} from 'lucide-react';

// UI Components
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import GoodsReceiptDocument from './GoodsreceiptDocument';

// Helpers
import { 
    fetchAllBankAccounts, 
    fetchVendorCredit, 
    goodsReceiptDocument 
} from '../apiHelper';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';

const client = new FastAPIClient();

const GoodsReceiptApproval = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const payload = location.state;
    const { showToast } = useToast();

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [receiptData, setReceiptData] = useState(null);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [availableCredit, setAvailableCredit] = useState(0);

    const [financials, setFinancials] = useState({
        discount_percent: 0,
        vat_percent: 0,
        payment_method: 'credit',
        invoice_due_date: new Date().toISOString().split('T')[0],
        currency: 'NGN'
    });

    const [payments, setPayments] = useState([
        { bankaccount_id: '', amount: '', is_credit_drawdown: false }
    ]);

    useEffect(() => {
        const init = async () => {
            if (!payload?.goodsreceipt_id) {
                navigate(-1);
                return;
            }
            try {
                const [doc, banks] = await Promise.all([
                    goodsReceiptDocument(payload),
                    fetchAllBankAccounts()
                ]);

                // ensure every item has a numeric cost field for the local state
                const hydratedDoc = {
                    ...doc,
                    items_received: doc.items_received.map(item => ({
                        ...item,
                        cost: item.cost || 0, // Initialize null costs to 0 for the inputs
                        listprice: item.listprice || 0,
                    }))
                };

                // console.log(doc)
                setReceiptData(hydratedDoc);
                setBankAccounts(banks.filter(b => b.name !== 'Wallet').map(b => ({ label: b.name, value: b.account_id })));
                const creditBalance = await fetchVendorCredit(doc.vendor_id);
                setAvailableCredit(Math.abs(creditBalance));
            } catch (err) {
                console.error("Initialization failed", err);
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, [payload, navigate]);

    const baseTotal = useMemo(() => {
        return receiptData?.items_received?.reduce((acc, item) => 
            acc + (item.cost * item.quantity_received), 0) || 0;
    }, [receiptData]);

    const grandTotal = useMemo(() => {
        const discountAmt = baseTotal * (financials.discount_percent / 100);
        const afterDiscount = baseTotal - discountAmt;
        const vatAmt = afterDiscount * (financials.vat_percent / 100);
        return afterDiscount + vatAmt;
    }, [baseTotal, financials.discount_percent, financials.vat_percent]);

    const totalPaid = useMemo(() => {
        return payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    }, [payments]);

    const remainingBalance = grandTotal - totalPaid;

    const applyCreditToPayment = () => {
        if (availableCredit <= 0 || payments.some(p => p.is_credit_drawdown)) return;
        const amountToApply = Math.min(availableCredit, grandTotal);
        const creditPayment = {
            bankaccount_id: 'CREDIT_ACCOUNT',
            amount: amountToApply.toString(),
            is_credit_drawdown: true,
            label: "Vendor Credit"
        };
        setPayments([creditPayment, ...payments.filter(p => p.amount !== '')]);
    };

    const resetForm = () => {
        setFinancials({
            discount_percent: 0,
            vat_percent: 0,
            payment_method: 'credit',
            invoice_due_date: new Date().toISOString().split('T')[0],
            currency: 'NGN'
        });
        setPayments([{ bankaccount_id: '', amount: '', is_credit_drawdown: false }]);
    };

    const handleItemChange = (podetail_id, field, value) => {
        setReceiptData(prev => ({
            ...prev, 
            items_received: prev.items_received.map(item => 
                item.podetail_id === podetail_id 
                    ? {...item, [field]: value === '' ? '' : parseFloat(value)} 
                    : item
            )
        }));
    };

    const handleApproval = async () => {
        // --- 1. Item Level Validations --- 
        for (const item of receiptData?.items_received || []) {
            if (item.cost === undefined || item.cost === null || item.cost <= 0) {
                showToast(`Please enter a valid unit cost (> 0) for item: "${item.productname || 'Unnamed item'}"`, 'error');
                return;
            }
            // if (item.listprice === undefined || item.listprice === null || item.listprice <= 0) {
            //     showToast(`Please enter a valid list price (> 0) for item: "${item.productname || 'Unnamed item'}"`, 'error');
            //     return;
            // }
        }

        // -- 2. Financial Config Validations -- 
        if (financials.discount_percent === '' || isNaN(financials.discount_percent) || financials.discount_percent < 0) {
            showToast("Please enter a valid discount percentage.",  "error");
            return;
        }
        if (financials.vat_percent === '' || isNaN(financials.vat_percent) || financials.vat_percent < 0) {
            showToast("Please enter a valid VAT percentage.", "error");
            return;
        }

        // --- 3. Payment Method Specific Validations
        if (financials.payment_method === 'credit') {
            if (!financials.invoice_due_date) {
                showToast("Please select an Invoice Due Date for credit purchases.", "error");
                return;
            }
        } else if (financials.payment_method === 'cash') {
            if (payments.length === 0) {
                showToast("Please add at least one payment source.", "error");
                return;
            }

            for (const payment of payments) {
                if (!payment.bankaccount_id) {
                    showToast("Please select a funding source / bank account for all payment entries.", "error");
                    return;
                }
                const parsedAmt = parseFloat(payment.amount);
                if (isNaN(parsedAmt) || parsedAmt <= 0) {
                    showToast("Please specify a valid payment amount (> 0) for all payment lines.", "error");
                    return;
                }
            }
        }

        setIsSubmitting(true);
        
        const submissionPayload = {
            items: receiptData.items_received.map(item => ({
                goodsreceipt_id: payload.goodsreceipt_id,
                catentry_id: item.catentry_id,
                qtyreceived: item.quantity_received,
                cost: parseFloat(item.cost),
                listprice: parseFloat(item.listprice),
                comment: item.comment || "",
            })),

            // goodsreceipt_id: payload.goodsreceipt_id,
            purchaseorder_id: payload.purchaseorder_id,
            vendor_id: receiptData?.vendor_id,

            summary: {
                base_total: parseFloat(baseTotal.toFixed(2)),
                discount_amount: parseFloat((baseTotal * (financials.discount_percent / 100)).toFixed(2)),
                vat_amount: parseFloat(((baseTotal - (baseTotal * (financials.discount_percent / 100))) * (financials.vat_percent / 100)).toFixed(2)),
                grand_total: parseFloat(grandTotal.toFixed(2)),
                currency: financials.currency,
            },

            configuration: {
                discount_percent: parseFloat(financials.discount_percent || 0),
                vat_percent: parseFloat(financials.vat_percent || 0),
                payment_method: financials.payment_method,
                invoice_due_date: financials.payment_method === 'credit' ? financials.invoice_due_date : null
            },

            payment_details: financials.payment_method === 'cash' ? payments.map(p => ({
                bankaccount_id: p.bankaccount_id,
                amount: parseFloat(p.amount),
                is_credit_drawdown: p.is_credit_drawdown,
            })) : []
        };

        try {
            // console.log("submitting approval payload: ", submissionPayload)
            const response = await client.postData("/vendor/approve-goods-receipt", submissionPayload)
            showToast(response.msg || "Receipt verified successfully", 'success');
            // resetForm();
            // Optional: navigate away after success
            // navigate(-1)
        } catch (error) {
            console.error("Submission failed:", error)
            showToast(error.response?.data?.detail || "Submission failed", 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) return (
        <div className="h-screen flex items-center justify-center bg-slate-50">
            <Loader2 className="animate-spin text-emerald-600" size={48} />
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col xl:flex-row relative">
            
            {/* LEFT PANE: Document Preview */}
            <div className="flex-grow p-4 lg:p-8">
                <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-6 transition-colors"
                >
                    <ArrowLeft size={18} /> Back to Pending
                </button>
                
                <Card className="max-w-3xl mx-auto p-6 lg:p-10 bg-white shadow-xl border-t-4 border-emerald-500">
                    <GoodsReceiptDocument data={receiptData} currency={financials.currency} />
                </Card>
                <Card className="max-w-3xl mx-auto mt-6 p-6 bg-white shadow-md border-l-4 border-blue-500">
                    <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                        <DollarSign size={16} /> Set Unit Costs
                    </h4>
                    <div className="space-y-4">
                        {receiptData?.items_received?.map((item) => (
                            <div key={item.podetail_id} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                                <p className="text-sm font-bold text-slate-700 mb-3">{item.productname}</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <Input
                                        label="Unit Cost"
                                        type="number"
                                        placeholder="0.00"
                                        value={item.cost || ''}
                                        onChange={(e) => handleItemChange(item.podetail_id, 'cost', e.target.value)}
                                    />
                                    <Input 
                                        label="New List Price"
                                        type="number"
                                        placeholder="0.00"
                                        value={item.listprice || ''}
                                        onChange={(e) => handleItemChange(item.podetail_id, 'listprice', e.target.value)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* RIGHT PANE: Financial Settlement */}
            <aside className="w-full xl:w-[500px] xl:sticky xl:top-0 xl:h-screen bg-slate-900 text-white shadow-2xl p-6 lg:p-8 flex flex-col overflow-y-auto border-l border-slate-800">
                <h3 className="text-xl font-bold mb-8 flex items-center gap-3 border-b border-slate-700 pb-4">
                    <CheckCircle2 className="text-emerald-400" /> Approval & Settlement
                </h3>

                {/* Section 1: Taxes & Discounts */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                    <Input
                        label="Discount (%)"
                        type="number"
                        className="bg-slate-800 border-slate-700 text-white focus:ring-emerald-500"
                        value={financials.discount_percent}
                        onChange={(e) => setFinancials({...financials, discount_percent: e.target.value})}
                    />
                    <Input
                        label="V.A.T (%)"
                        type="number"
                        className="bg-slate-800 border-slate-700 text-white focus:ring-emerald-500"
                        value={financials.vat_percent}
                        onChange={(e) => setFinancials({...financials, vat_percent: e.target.value})}
                    />
                </div>

                {/* Section 2: Payment Method Toggle */}
                <div className="flex items-center gap-4 bg-slate-800 p-2 rounded-xl border border-slate-700 mb-8">
                    <label className={`flex-1 flex items-center justify-center gap-2 cursor-pointer p-3 rounded-lg transition-all 
                        ${financials.payment_method === 'cash' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-700'}`}
                    >
                        <input type="radio" className="hidden" name="payMethod" 
                            onChange={() => setFinancials({...financials, payment_method: 'cash'})} />
                        <span className="font-bold text-sm">Immediate Cash</span>
                    </label>
                    <label className={`flex-1 flex items-center justify-center gap-2 cursor-pointer p-3 rounded-lg transition-all 
                        ${financials.payment_method === 'credit' ? 'bg-amber-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-700'}`}
                    >
                        <input type="radio" className="hidden" name="payMethod" 
                            onChange={() => setFinancials({...financials, payment_method: 'credit'})} />
                        <span className="font-bold text-sm">Credit Purchase</span>
                    </label>
                </div>

                {/* Section 3: Dynamic Payment Entry */}
                <div className="flex-grow space-y-4">
                    {financials.payment_method === 'cash' ? (
                        <>
                            {availableCredit > 0 && !payments.some(p => p.is_credit_drawdown) && (
                                <div className="p-3 bg-emerald-900/40 border border-emerald-700 rounded-lg flex justify-between items-center animate-pulse">
                                    <span className="text-xs font-bold uppercase tracking-wider">
                                        Vendor Credit: {financials.currency} {availableCredit.toLocaleString()}
                                    </span>
                                    <Button size="xs" variant="primary" onClick={applyCreditToPayment}>Apply</Button>
                                </div>
                            )}

                            {payments.map((p, i) => (
                                <div key={i} className={`flex gap-3 items-end p-4 rounded-xl border transition-all focus-within:ring-2 focus-within:ring-emerald-500/50 ${p.is_credit_drawdown ? 'bg-emerald-900/20 border-emerald-500/50' : 'bg-slate-800/50 border-slate-700'}`}>
                                    <div className="flex-1">
                                        <label className="text-[10px] uppercase font-bold text-slate-400 mb-2 block tracking-widest">Funding Source</label>
                                        <select 
                                            required
                                            className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                                            value={p.bankaccount_id}
                                            disabled={p.is_credit_drawdown}
                                            onChange={(e) => {
                                                const up = [...payments];
                                                up[i].bankaccount_id = e.target.value;
                                                setPayments(up);
                                            }}
                                        >
                                            <option value="" className="bg-slate-900">Select Account...</option>
                                            {bankAccounts.map(b => <option key={b.value} value={b.value} className="bg-slate-900">{b.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="w-36">
                                        <label className="text-[10px] uppercase font-bold text-slate-400 mb-2 block tracking-widest">Amount</label>
                                        <input
                                            type="number"
                                            value={p.amount}
                                            placeholder="0.00"
                                            readOnly={p.is_credit_drawdown}
                                            className={`w-full p-2 rounded-md border text-right font-mono text-lg transition-all focus:outline-none focus:ring-1 focus:ring-emerald-500
                                                ${p.is_credit_drawdown 
                                                    ? 'bg-emerald-800/20 border-emerald-600/50 text-emerald-100 cursor-not-allowed' 
                                                    : 'bg-slate-900 border-slate-600 text-white hover:border-slate-500 focus:border-emerald-500'
                                                }`}
                                            onChange={(e) => {
                                                const up = [...payments];
                                                up[i].amount = e.target.value;
                                                setPayments(up);
                                            }}
                                        />
                                    </div>
                                    {payments.length > 1 && (
                                        <button onClick={() => setPayments(payments.filter((_, pi) => pi !== i))} className="text-red-400 hover:text-red-300 p-2 hover:bg-red-950/30 rounded-full transition-all">
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button 
                                onClick={() => setPayments([...payments, { bankaccount_id: '', amount: '', is_credit_drawdown: false }])}
                                className="text-[11px] text-blue-400 font-black hover:text-blue-300 uppercase tracking-widest mt-2 flex items-center gap-2 p-2 rounded-lg hover:bg-blue-400/10 transition-all w-fit"
                            >
                                <Zap size={14} className="fill-current" /> Add Payment Source
                            </button>
                        </>
                    ) : (
                        <div className="p-5 bg-amber-900/20 border border-amber-800/50 rounded-xl">
                            <div className="flex gap-3 mb-5">
                                <AlertCircle className="text-amber-500 shrink-0" size={20} />
                                <p className="text-sm text-amber-100 leading-relaxed">
                                    Logging as <strong>Accounts Payable</strong>. Full amount will be added to vendor debt.
                                </p>
                            </div>
                            <Input
                                label="Invoice Due Date"
                                type="date"
                                className="bg-slate-800 border-slate-700 text-white focus:ring-amber-500"
                                value={financials.invoice_due_date}
                                onChange={(e) => setFinancials({...financials, invoice_due_date: e.target.value})}
                            />
                        </div>
                    )}
                </div>

                {/* Footer Section */}
                <div className="mt-10 pt-6 border-t border-slate-700">
                    <div className="flex justify-between items-end mb-6">
                        <div className="text-left">
                            <p className="text-slate-400 text-[10px] uppercase font-black tracking-widest leading-none">Net Total Payable</p>
                            <p className="text-5xl font-black text-white leading-none mt-2">
                                <span className="text-sm font-normal text-slate-500 mr-1">{financials.currency}</span>
                                {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                        </div>
                        {financials.payment_method === 'cash' && (
                            <div className="text-right">
                                <p className="text-[10px] uppercase font-bold text-slate-500 tracking-tighter">Due</p>
                                <p className={`text-xl font-mono font-bold ${remainingBalance <= 0.01 ? 'text-emerald-400' : 'text-orange-400'}`}>
                                    {remainingBalance.toLocaleString()}
                                </p>
                            </div>
                        )}
                    </div>

                    <Button
                        fullWidth
                        size="lg"
                        className="py-6 text-xl font-black bg-emerald-600 hover:bg-emerald-500 shadow-xl shadow-emerald-900/20 rounded-xl transition-all active:scale-95"
                        disabled={isSubmitting || (financials.payment_method === 'cash' && remainingBalance > 0.01)}
                        onClick={handleApproval}
                    >
                        {isSubmitting ? (
                            <div className="flex items-center gap-2">
                                <Loader2 className="animate-spin" /> <span>PROCESSING</span>
                            </div>
                        ) : "VERIFY & APPROVE RECEIPT"}
                    </Button>
                </div>
            </aside>
        </div>
    );
};

export default GoodsReceiptApproval;